"use client";

import type { PropsWithChildren } from "react";
import { Suspense, useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";

import { RwgSync } from "~/components/auth/RwgSync";
import { redirectToUpdatedTermsIfPending } from "~/queries/profile/profile.service";
import { useProfileSSOid } from "~/queries/profile/useProfile";

/**
 * Reproduces the mount-time side effects the removed `ProfileDataProvider` ran,
 * now driven by the TanStack Query cache instead of Context state: kicks off the
 * profile fetches, redirects to updated-terms when pending, sets
 * the Datadog user, and syncs the RWG token. (Logging out on an invalid session
 * lives in the profile queryFn, `fetchProfile`, not here.) Renders children unchanged, so it slots into the provider list in place of the
 * old provider.
 */
export const ProfileProvider = ({ children }: PropsWithChildren) => {
  // Fetches the profile if we have the access token
  const ssoId = useProfileSSOid();

  // Set the Datadog user once the profile loads.
  useEffect(() => {
    if (ssoId && datadogRum.getUser()?.id !== ssoId) {
      datadogRum.setUser({ id: ssoId });
    }
  }, [ssoId]);

  // When the user is logged in — on mount or right after login — check for a
  // pending updated-terms experience and redirect to it if present.
  useEffect(() => {
    if (!ssoId) return;

    redirectToUpdatedTermsIfPending();
    // We only want to recheck on the ssoId - localeString shouldn't retrigger this
  }, [ssoId]);

  return (
    <>
      <Suspense>
        <RwgSync />
      </Suspense>
      {children}
    </>
  );
};
